import { Layers, Cpu, BookOpen, Workflow } from "lucide-react";

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative scroll-mt-28 border-y border-slate-200 bg-white py-24 md:py-32"
    >
      <div className="ct-container">
        <div className="grid items-end gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <span className="ct-eyebrow">What we build</span>
            <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-[#0F172A] sm:text-5xl">
              Content + AI,
              <br />
              <span className="text-[#737373]">engineered together.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-base leading-relaxed text-slate-600">
            We approach every product as a system — where content, data, and
            AI compound. Below is how we think about the work.
          </p>
        </div>

        {/* Bento grid */}
        <div className="mt-14 grid gap-4 md:grid-cols-6 md:grid-rows-3">
          {/* Tile 1 — Big hero tile */}
          <article
            data-testid="service-content-engineering"
            className="group relative overflow-hidden rounded-3xl border border-neutral-200 bg-[#FAFAFA] p-8 md:col-span-4 md:row-span-3 md:p-10"
          >
            <div className="flex items-start justify-between">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#0A0A0A] text-white">
                <Layers className="h-5 w-5" />
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                01 / Core
              </span>
            </div>
            <h3 className="mt-8 font-display text-2xl font-semibold leading-tight text-[#0F172A] md:text-3xl">
              Content Engineering
            </h3>
            <p className="mt-3 max-w-md text-base leading-relaxed text-slate-600">
              Structured, scalable content systems — designed for clarity,
              retrievability, and learning outcomes.
            </p>

            {/* Mini graphic — content tree */}
            <div className="mt-10 select-none font-mono text-[11px] leading-6 text-neutral-500">
              <div>curriculum/</div>
              <div className="ml-3">├── concepts/</div>
              <div className="ml-6">│   ├── foundations.md</div>
              <div className="ml-6">│   └── advanced.md</div>
              <div className="ml-3">├── assessments/</div>
              <div className="ml-6">│   └── adaptive.json</div>
              <div className="ml-3">└── pipelines/</div>
              <div className="ml-6 text-[#0A0A0A]">    └── ai_review.ts ●</div>
            </div>
          </article>

          {/* Tile 2 — Applied AI */}
          <article
            data-testid="service-applied-ai-automation"
            className="group relative overflow-hidden rounded-3xl border border-neutral-200 bg-white p-7 md:col-span-2 md:row-span-1"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#0A0A0A] text-white">
                <Cpu className="h-5 w-5" />
              </span>
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">
                Applied AI
              </h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Workflows that compress effort, personalize delivery, and increase reach.
            </p>
            {/* mini network graphic */}
            <svg viewBox="0 0 120 40" className="mt-4 h-10 w-full text-neutral-400">
              <g stroke="currentColor" strokeWidth="1" fill="none">
                <line x1="10" y1="20" x2="40" y2="8" />
                <line x1="10" y1="20" x2="40" y2="32" />
                <line x1="40" y1="8" x2="80" y2="20" />
                <line x1="40" y1="32" x2="80" y2="20" />
                <line x1="80" y1="20" x2="110" y2="12" />
                <line x1="80" y1="20" x2="110" y2="28" />
              </g>
              <g fill="#0A0A0A">
                <circle cx="10" cy="20" r="2.5" />
                <circle cx="40" cy="8" r="2.5" />
                <circle cx="40" cy="32" r="2.5" />
                <circle cx="80" cy="20" r="2.5" />
                <circle cx="110" cy="12" r="2.5" />
                <circle cx="110" cy="28" r="2.5" />
              </g>
            </svg>
          </article>

          {/* Tile 3 — Learning Platforms */}
          <article
            data-testid="service-learning-platforms"
            className="group relative overflow-hidden rounded-3xl border border-neutral-200 bg-white p-7 md:col-span-2 md:row-span-1"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#0A0A0A] text-white">
                <BookOpen className="h-5 w-5" />
              </span>
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">
                Learning Platforms
              </h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              End-to-end platforms — from curriculum to delivery — built around trust.
            </p>
            {/* layered stack graphic */}
            <div className="mt-4 space-y-1.5">
              <div className="h-2 w-full rounded-sm bg-neutral-200" />
              <div className="h-2 w-5/6 rounded-sm bg-neutral-300" />
              <div className="h-2 w-2/3 rounded-sm bg-neutral-400" />
              <div className="h-2 w-1/2 rounded-sm bg-[#0A0A0A]" />
            </div>
          </article>

          {/* Tile 4 — Digital Ecosystems */}
          <article
            data-testid="service-digital-ecosystems"
            className="group relative overflow-hidden rounded-3xl border border-neutral-200 bg-[#0A0A0A] p-7 text-neutral-100 md:col-span-2 md:row-span-1"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-white text-[#0A0A0A]">
                <Workflow className="h-5 w-5" />
              </span>
              <h3 className="font-display text-lg font-semibold text-white">
                Digital Ecosystems
              </h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-neutral-400">
              Architected for performance, accessibility, security, and reliability.
            </p>
            {/* orbit graphic */}
            <svg viewBox="0 0 120 50" className="mt-4 h-10 w-full">
              <g fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1">
                <ellipse cx="60" cy="25" rx="50" ry="14" />
                <ellipse cx="60" cy="25" rx="35" ry="9" />
              </g>
              <circle cx="60" cy="25" r="3" fill="white" />
              <circle cx="10" cy="25" r="2" fill="white" />
              <circle cx="95" cy="34" r="2" fill="rgba(255,255,255,0.6)" />
              <circle cx="40" cy="16" r="1.6" fill="rgba(255,255,255,0.5)" />
            </svg>
          </article>
        </div>
      </div>
    </section>
  );
}
